import { useQueryClient } from '@tanstack/react-query';

import type { VesselMessage } from '../../types/VesselMessage';
import { AsyncComboBox } from '../cdl/ComboBox/ComboBox';
import type { AsyncComboBoxProps, ComboBoxOption } from '../cdl/ComboBox/ComboBox';
import { getVesselList } from '../common/api/clients/vessel.api';
import type { GetVesselListParams } from '../common/api/clients/vessel.api';
import { queries } from '../common/api/queryKeys/queries';
import { formatVessel } from '../common/helpers/formatVessel.helper';
import { translate } from '../common/helpers/translate.helper';
import { useCustomerIdList } from '../keyport/useCustomerIdList';

interface VesselOption extends ComboBoxOption {
    vessel: VesselMessage;
}

const toVesselOption = (vessel: VesselMessage): VesselOption => ({
    label: formatVessel({ vessel }),
    value: vessel.id,
    vessel,
});

export interface VesselSelectProps
    extends Omit<
        AsyncComboBoxProps<VesselOption>,
        'value' | 'onChange' | 'optionsQueryKey' | 'optionsQueryFn' | 'selectedOptionQueryKey' | 'selectedOptionQueryFn'
    > {
    vesselId: string | null;
    groupId?: string;
    customerIds?: string[];
    active?: boolean;
    imo?: string;
    onChange?: (vessel: VesselMessage | null) => void;
}

export const VesselSelect = ({
    vesselId,
    groupId,
    customerIds,
    active = true,
    imo,
    onChange,
    label = translate('keyPorts.vessel'),
    ...props
}: VesselSelectProps) => {
    const queryClient = useQueryClient();
    const { data: customerIdsFromGroup = [] } = useCustomerIdList({
        groupId: groupId ?? undefined,
        enabled: groupId !== undefined,
    });
    const effectiveCustomerIds = customerIds ?? customerIdsFromGroup;

    const getVesselListParams = (searchQuery: string): GetVesselListParams => ({
        searchQuery,
        customerIds: effectiveCustomerIds.length ? effectiveCustomerIds : undefined,
        active,
        imo,
    });

    const loadVessels = async (searchQuery: string): Promise<VesselOption[]> => {
        const hasCustomerScope = customerIds !== undefined || groupId !== undefined;
        if (hasCustomerScope && effectiveCustomerIds.length === 0) {
            return [];
        }

        const vessels = await getVesselList(getVesselListParams(searchQuery));

        return vessels.map(toVesselOption);
    };

    const loadVessel = async (id: string): Promise<VesselOption | null> => {
        const vessel = await queryClient.fetchQuery(queries.vessels.detail(id));
        return vessel ? toVesselOption(vessel) : null;
    };

    return (
        <AsyncComboBox<VesselOption>
            {...props}
            label={label}
            value={vesselId || null}
            optionsQueryKey={(searchQuery) =>
                queries.vessels.comboBoxOptions(getVesselListParams(searchQuery)).queryKey
            }
            optionsQueryFn={loadVessels}
            selectedOptionQueryKey={(id) => queries.vessels.selectedComboBoxOption(id).queryKey}
            selectedOptionQueryFn={loadVessel}
            onChange={(_, meta) => {
                const vessel = meta.option?.vessel ?? null;

                if (vessel) {
                    queryClient.setQueryData(queries.vessels.detail(vessel.id).queryKey, vessel);
                }

                onChange?.(vessel);
            }}
        />
    );
};
